/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import ActionsMenu from '../../../shared/ActionsMenu'
import { useServiceContext } from '../../../../root'
import { useModal } from '../../../../contexts/ModalContext'
import { SystemFolderType } from '../../../../constants/fsi'
import ShareReportModal from '../../../../pages/browser/ShareReportModal'
import FavoriteNameModal from '../../../../pages/browser/FavoriteNameModal'
import MakeObsoleteModal from '../../../../pages/browser/MakeObsoleteModal'
import MakeUnobsoleteModal from '../../../../pages/browser/MakeUnobsoleteModal'
import FolderAndDocumentDetailsModal from '../../../modals/FolderAndDocumentDetailsModal'
import RenameFolderAndDocumentModal from '../../../../pages/browser/RenameModal'
import { getFileDetail, getFilePath, getFileDownloadUrl, removeFileFromFavorite } from '../../../../services/api/files'

// Loading placeholder constant
const LOADING_MENU_PLACEHOLDER = {
  value: 'LOADING...',
  disabled: true
}

interface ReportActionMenuProps {
  fsi: any
  options?: any
}

// Report action menu for homepage reports table - mirrors browser ReportsTableMenu pattern
export const ReportActionMenu: React.FC<ReportActionMenuProps> = ({ fsi, options = {} }) => {
  const { api } = useServiceContext()
  const { showModal } = useModal()
  const [menuOptions, setMenuOptions] = useState<any[]>([LOADING_MENU_PLACEHOLDER])
  const [isOpen, setIsOpen] = useState(false)

  const configureAndSetMenuOptions = (file: any) => {
    const menu: any[] = []

    menu.push({
      value: 'BROWSER_ACTION_VIEW_DETAILS',
      action: () => {
        showModal(<FolderAndDocumentDetailsModal fileDetails={file} versionInfo={{}} isReport={true} />)
      }
    })

    if (file.actionFlags?.canDownloadSource) {
      menu.push({
        value: 'DOWNLOAD',
        action: async () => {
          try {
            const data = await getFileDownloadUrl(Number(file.id))
            if (data.url) {
              window.location.href = data.url
            }
          } catch (error) {
            console.error('Error downloading report:', error)
          }
        }
      })
    }

    // share
    if (file.actionFlags?.canManageAcl) {
      menu.push({
        value: 'BROWSER_ACTION_SHARE_REPORT',
        action: () => {
          showModal(<ShareReportModal file={file} onClose={() => {}} />)
        }
      })
    }

    // rename
    if (file.actionFlags?.canRename) {
      menu.push({
        value: 'BROWSER_ACTION_RENAME',
        action: () => {
          showModal(<RenameFolderAndDocumentModal fileDetails={file} />)
        }
      })
    }

    // make obsolete / unobsolete
    if (file.obsolete && file.actionFlags?.canUnobsolete) {
      menu.push({
        value: 'BROWSER_ACTION_MAKE_UNOBSOLETE',
        action: () => {
          showModal(<MakeUnobsoleteModal fileDetails={file} />)
        }
      })
    } else if (file.actionFlags?.canObsolete) {
      menu.push({
        value: 'BROWSER_ACTION_MAKE_OBSOLETE',
        action: () => {
          showModal(<MakeObsoleteModal fileDetails={file} />)
        }
      })
    }

    // favorites
    let showFavMenu = true
    if (file.systemFolderType === SystemFolderType.WORKFLOWS || file.obsolete || file.id === -1) {
      showFavMenu = false
    }

    if (showFavMenu) {
      if (file.isFavorite) {
        menu.push({
          value: 'BROWSER_ACTION_REMOVE_FROM_FAV',
          action: async () => {
            await removeFileFromFavorite(file.id)
            window.dispatchEvent(new CustomEvent('updateFiles'))
            window.dispatchEvent(new Event('updateFavorites'))
          }
        })
      } else {
        menu.push({
          value: 'BROWSER_ACTION_ADD_TO_FAV',
          action: () => {
            showModal(<FavoriteNameModal fileDetails={file} onClose={() => {}} />)
          }
        })
      }
    }

    setMenuOptions(menu)
  }

  useEffect(() => {
    if (!fsi || !fsi.id) {
      return
    }

    if (isOpen) {
      // If actionFlags already exist on the fsi, use them directly
      if (fsi.actionFlags) {
        configureAndSetMenuOptions(fsi)
        return
      }

      // Otherwise, fetch file details and path from the API (lazy load)
      Promise.all([getFileDetail(fsi.id), getFilePath(fsi.id)]).then(([details, path]) => {
        configureAndSetMenuOptions({
          ...details,
          path
        })
      })
    }
  }, [fsi, isOpen])

  if (!fsi || !fsi.id) {
    return null
  }

  return (
    <ActionsMenu
      menuId={`report-${fsi.id}`}
      onToggle={(open: boolean) => {
        setIsOpen(open)
      }}
      items={menuOptions}
    />
  )
}
